import { createComponentStore } from '../../utils/createComponentStore';
import { parseUrlParams } from './httpRequestUtils';

const EMPTY_ROW = () => ({ key: '', value: '', enabled: true });

const emptyTemplateData = () => ({
  appName: '',
  action: { appName: '', action: '', description: '', requirements: [], endpoint: '', curl: '', fields: [] },
  credentialPlaceholder: '',
});

const emptyData = () => ({
  method: 'GET',
  url: '',
  headers: [EMPTY_ROW()],
  queryParams: [EMPTY_ROW()],
  body: '',
  bodyType: 'none',
  authType: 'none',
  auth: {
    token: '',
    apiKeyName: '',
    apiKeyValue: '',
    apiKeyLocation: 'header',
    username: '',
    password: '',
    credentialId: '',
  },
  timeoutSeconds: 30,
  outputKey: 'responseData',
  followRedirects: true,
  templateData: emptyTemplateData(),
});

const { getStore: getHttpRequestStore, defaultStore } = createComponentStore({
  type: 'http_request',
  endpoint: 'httprequest',
  registryKey: 'http_request',
  initData: emptyData,
  onLoadData: (rawData) => {
    const mapped = { ...rawData };
    // v1 → v2 field compat mapping
    if (mapped.responseOutputPlaceholder != null && mapped.outputKey == null) {
      mapped.outputKey = mapped.responseOutputPlaceholder;
      delete mapped.responseOutputPlaceholder;
    }
    return {
    ...emptyData(),
    ...mapped,
    headers: mapped.headers?.length ? mapped.headers : [EMPTY_ROW()],
    queryParams: mapped.queryParams?.length ? mapped.queryParams : [EMPTY_ROW()],
    auth: { ...emptyData().auth, ...(mapped.auth || {}) },
    templateData: mapped.templateData && mapped.templateData.appName
      ? { ...emptyTemplateData(), ...mapped.templateData }
      : emptyTemplateData(),
  }; },
  extraActions: (set, get) => ({
    setMethod: (method) => set({ data: { ...get().data, method } }),

    /**
     * Sets the URL. If it contains a query string (?key=val&...),
     * the params are extracted and merged into queryParams (Postman-style sync).
     */
    setUrl: (url) => {
      const { baseUrl, params } = parseUrlParams(url);
      if (params.length > 0) {
        const existing = get().data.queryParams.filter((p) => p.key?.trim());
        const merged = [...existing, ...params, EMPTY_ROW()];
        set({ data: { ...get().data, url: baseUrl, queryParams: merged } });
      } else {
        set({ data: { ...get().data, url } });
      }
    },

    /**
     * Sets the base URL without parsing query params (used internally
     * to avoid infinite loops when params tab updates the display URL).
     */
    setBaseUrl: (url) => set({ data: { ...get().data, url } }),

    setHeaders: (headers) => set({ data: { ...get().data, headers } }),
    setQueryParams: (queryParams) => set({ data: { ...get().data, queryParams } }),
    setBody: (body) => set({ data: { ...get().data, body } }),
    setBodyType: (bodyType) => set({ data: { ...get().data, bodyType } }),
    setAuthType: (authType) => set({ data: { ...get().data, authType } }),
    setAuth: (auth) => set({ data: { ...get().data, auth: { ...get().data.auth, ...auth } } }),
    setTimeoutSeconds: (timeoutSeconds) => set({ data: { ...get().data, timeoutSeconds } }),
    setOutputKey: (outputKey) =>
      set({ data: { ...get().data, outputKey } }),
    // Backward-compat alias
    setResponseOutputPlaceholder: (responseOutputPlaceholder) =>
      set({ data: { ...get().data, outputKey: responseOutputPlaceholder } }),
    setFollowRedirects: (followRedirects) => set({ data: { ...get().data, followRedirects } }),

    updateHeader: (index, field, value) => {
      const headers = [...get().data.headers];
      headers[index] = { ...headers[index], [field]: value };
      set({ data: { ...get().data, headers } });
    },
    addHeader: () => {
      const headers = [...get().data.headers, EMPTY_ROW()];
      set({ data: { ...get().data, headers } });
    },
    removeHeader: (index) => {
      const headers = get().data.headers.filter((_, i) => i !== index);
      set({ data: { ...get().data, headers: headers.length ? headers : [EMPTY_ROW()] } });
    },

    updateQueryParam: (index, field, value) => {
      const queryParams = [...get().data.queryParams];
      queryParams[index] = { ...queryParams[index], [field]: value };
      set({ data: { ...get().data, queryParams } });
    },
    addQueryParam: () => {
      const queryParams = [...get().data.queryParams, EMPTY_ROW()];
      set({ data: { ...get().data, queryParams } });
    },
    removeQueryParam: (index) => {
      const queryParams = get().data.queryParams.filter((_, i) => i !== index);
      set({ data: { ...get().data, queryParams: queryParams.length ? queryParams : [EMPTY_ROW()] } });
    },

    importFromCurl: (parsed) => {
      const data = get().data;
      const base = emptyData();

      // Extract auth from headers
      let authType = 'none';
      let auth = { ...base.auth };
      const filteredHeaders = [];
      if (parsed.headers?.length) {
        for (const h of parsed.headers) {
          const key = (h.key || '').toLowerCase();
          if (key === 'authorization') {
            const val = h.value || '';
            if (val.toLowerCase().startsWith('bearer ')) {
              authType = 'bearer';
              auth = { ...auth, token: val.substring(7) };
            } else if (val.toLowerCase().startsWith('basic ')) {
              authType = 'basic';
              auth = { ...auth, token: val.substring(6) };
            } else {
              filteredHeaders.push(h);
            }
          } else {
            filteredHeaders.push(h);
          }
        }
      }

      // Detect body type from Content-Type header
      let bodyType = 'none';
      if (parsed.body) {
        const contentTypeHeader = (parsed.headers || []).find(
          (h) => (h.key || '').toLowerCase() === 'content-type'
        );
        const ct = (contentTypeHeader?.value || '').toLowerCase();
        if (ct.includes('application/json')) {
          bodyType = 'json';
        } else if (ct.includes('x-www-form-urlencoded')) {
          bodyType = 'x-www-form-urlencoded';
        } else if (ct.includes('form-data')) {
          bodyType = 'form-data';
        } else if (ct.includes('text/')) {
          bodyType = 'text';
        } else {
          bodyType = 'json';
        }
      }

      // Replace the entire request — clean slate, not a merge
      set({
        data: {
          ...base,
          method: parsed.method || 'GET',
          url: parsed.url || '',
          headers: filteredHeaders.length ? [...filteredHeaders, EMPTY_ROW()] : [EMPTY_ROW()],
          queryParams: parsed.queryParams?.length
            ? [...parsed.queryParams, EMPTY_ROW()]
            : [EMPTY_ROW()],
          body: parsed.body || '',
          bodyType,
          authType,
          auth,
          // Preserve non-request settings
          timeoutSeconds: data.timeoutSeconds,
          outputKey: data.outputKey,
          followRedirects: data.followRedirects,
          templateData: data.templateData,
        },
      });
    },

    setTemplateData: (templateData) =>
      set({ data: { ...get().data, templateData: { ...get().data.templateData, ...templateData } } }),

    clearTemplateData: () =>
      set({ data: { ...get().data, templateData: emptyTemplateData() } }),
  }),
});

export { getHttpRequestStore };
export default defaultStore;
